/**
 * Class representing a resize listener for FeBlazorLibrary.
 */
export class ResizeListener {
    private logger: (message?: any, ...optionalParams: any[]) => void;
    private options: Record<string, any>;
    private throttleResizeHandlerId: number;
    private dotnet: any;
    private breakpoint: number;
    private id: string;
    private handleResize: () => void;

    /**
     * Creates an instance of ResizeListener.
     * @param {string} id - The identifier for the listener.
     */
    constructor(id: string | null = null) {
        /** @type {Function} */
        this.logger = function (message) { };
        /** @type {Object} */
        this.options = {};
        /** @type {number} */
        this.throttleResizeHandlerId = -1;
        /** @type {any} */
        this.dotnet = undefined;
        /** @type {number} */
        this.breakpoint = -1;
        /** @type {string} */
        this.id = id;
        this.handleResize = this.throttleResizeHandler.bind(this);
    }

    /**
     * Starts listening for resize events.
     * @param {any} dotnetRef - The .NET reference object.
     * @param {Object} options - The options for the listener.
     */
    listenForResize(dotnetRef: any, options: Record<string, any>): void {
        if (this.dotnet) {
            this.options = options;
            return;
        }

        this.options = options;
        this.dotnet = dotnetRef;
        this.logger = options.enableLogging ? console.log : (message) => { };
        this.logger(`[FeBlazorLibrary] Reporting resize events at rate of: ${this.options.reportRate}ms`);
        window.addEventListener("resize", this.handleResize, false);
        if (!this.options.suppressInitEvent) {
            this.resizeHandler();
        }
        this.breakpoint = this.getBreakpoint(window.innerWidth);
    }

    /**
     * Throttles the resize handler to limit the rate of resize events.
     */
    throttleResizeHandler(): void {
        clearTimeout(this.throttleResizeHandlerId);
        this.throttleResizeHandlerId = window.setTimeout(
            this.resizeHandler.bind(this),
            this.options.reportRate
        );
    }

    /**
     * Handles the resize event and notifies the .NET side.
     */
    resizeHandler(): void {
        if (this.options.notifyOnBreakpointOnly) {
            let bp = this.getBreakpoint(window.innerWidth);
            if (bp == this.breakpoint) {
                return;
            }
            this.breakpoint = bp;
        }

        try {
            if (this.id) {
                this.dotnet.invokeMethodAsync('RaiseOnResized',
                    {
                        height: window.innerHeight,
                        width: window.innerWidth
                    },
                    this.getBreakpoint(window.innerWidth),
                    this.id);
            } else {
                this.dotnet.invokeMethodAsync('RaiseOnResized',
                    {
                        height: window.innerHeight,
                        width: window.innerWidth
                    },
                    this.getBreakpoint(window.innerWidth));
            }

        } catch (error) {
            this.logger("[FeBlazorLibrary] Error in resizeHandler:", { error });
        }
    }

    /**
     * Cancels the resize listener.
     */
    cancelListener(): void {
        this.dotnet = undefined;
        window.removeEventListener("resize", this.handleResize);
    }

    /**
     * Matches the media query.
     * @param {string} query - The media query string.
     * @returns {boolean} - True if the media query matches, otherwise false.
     */
    matchMedia(query: string): boolean {
        let m = window.matchMedia(query).matches;
        return m;
    }

    /**
     * Gets the current browser window size.
     * @returns {Object} - The height and width of the window.
     */
    getBrowserWindowSize(): { height: number, width: number } {
        return {
            height: window.innerHeight,
            width: window.innerWidth
        };
    }

    /**
     * Gets the breakpoint based on the window width.
     * @param {number} width - The width of the window.
     * @returns {number} - The breakpoint value.
     */
    getBreakpoint(width: number): number {
        if (width >= this.options.breakpointDefinitions["Xxl"])
            return 5;
        else if (width >= this.options.breakpointDefinitions["Xl"])
            return 4;
        else if (width >= this.options.breakpointDefinitions["Lg"])
            return 3;
        else if (width >= this.options.breakpointDefinitions["Md"])
            return 2;
        else if (width >= this.options.breakpointDefinitions["Sm"])
            return 1;
        else //Xs
            return 0;
    }
}

/**
 * Class representing a factory for creating and managing ResizeListener instances.
 */
export class ResizeListenerFactory {
    private mapping: { [key: string]: ResizeListener };

    constructor() {
        /** @type {Object.<string, ResizeListener>} */
        this.mapping = {};
    }

    /**
     * Starts listening for resize events for a specific listener.
     * @param {any} dotnetRef - The .NET reference object.
     * @param {Object} options - The options for the listener.
     * @param {string} id - The identifier for the listener.
     */
    listenForResize(dotnetRef: any, options: Record<string, any>, id: string): void {
        if (this.mapping[id]) {
            return;
        }

        var listener = new ResizeListener(id);
        listener.listenForResize(dotnetRef, options);
        this.mapping[id] = listener;
    }

    /**
     * Cancels the resize listener for a specific listener.
     * @param {string} id - The identifier for the listener.
     */
    cancelListener(id: string): void {
        if (!this.mapping[id]) {
            return;
        }

        var listener = this.mapping[id];
        listener.cancelListener();
        delete this.mapping[id];
    }

    /**
     * Cancels the resize listeners for multiple listeners.
     * @param {string[]} ids - The identifiers for the listeners.
     */
    cancelListeners(ids: string[]): void {
        for (let i = 0; i < ids.length; i++) {
            this.cancelListener(ids[i]);
        }
    }

    /**
     * Disposes of all resize listeners.
     */
    dispose(): void {
        for (var id in this.mapping) {
            this.cancelListener(id);
        }
    }
}
